import {
  getPredictableZombies,
  DatastoreObjectType,
  DeviceTypes,
  MapLike,
  Networks,
  StandardProceessEnv,
  ValidBusinessObject,
  ValidBusinessObjectList,
  isIGeofence,
  isPosition,
} from '@iotv/iotv-v3-types'
import { GeofencePointsType, isGeofencePointsType } from '../types/ModelTypes'
import { convertGPointsToTPosition } from './geo/TurfUtils'
import { lineString } from '@turf/helpers'
import { length } from '@turf/length'
import { ThingShadowStateZombie } from '../types/DatastoreTypes'
import { getNLengthHex } from './hex'
import { reKeyItem } from './objects/identity'
import { NativeAttributeValue } from '@aws-sdk/util-dynamodb'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from './lambda'
import { v4 } from 'uuid'

export { getZombieInstanceFromPrimaryKey, getPredictableZombies, getZombieEdge } from '@iotv/iotv-v3-types'

type Key = Record<string, NativeAttributeValue>

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

const { projectCode, stage } = process.env as StandardProceessEnv

export const getMinimalVOB = (vob: ValidBusinessObject, additionalKeys?: string[]) => {
  const { sk, pk, name, id, type } = vob

  const res: ValidBusinessObject = { sk, pk, name, id, type }

  if (additionalKeys) {
    additionalKeys.forEach((k) => {
      if (vob[k]) {
        res[k] = vob[k]
      }
    })
  }

  return res
}

//** v-0-2-0 hack?? */
/** DEPRECATION WARNING
 * This vxxx (20) hack is specific to devices and will be removed
 */
export const getZombieInstanceFromAttributes = (pk: string, sk?: string, networkKey?: string, applicationId?: string): ValidBusinessObject => {
  if (!sk) {
    sk = pk
  }
  const [type, ...idOthers] = pk.split(':')
  const id = idOthers.join(':')
  const zombie: ValidBusinessObject = { type, id, sk: sk, pk: pk }
  zombie.appID = applicationId
  zombie.networkKey = networkKey
  return zombie
}

export const getDatastoreObjectKeyFromPrimaryKey = (pk: string): DatastoreObjectType => {
  const key = { pk, sk: pk }
  return key
}

export const getDynamoKeyFromPrimaryKey = (pk: string): Key => {
  const key = { pk, sk: pk }
  return key
}

/** Gets the key for the primary object. DOES NOT WORK FOR EDGES AS THIS WAS ORIGINALLY DONE AS SHORTCUT */
export const getDynamoKeyFromVob = (vob: ValidBusinessObject): Key => {
  const key = { pk: vob.pk, sk: vob.pk }
  return key
}

export const getDynamoKeyFromNodeOrEdge = (vob: ValidBusinessObject): Key => {
  const key = { pk: vob.pk, sk: vob.sk }
  return key
}

export const getDynamoKeysFromVobs = (vobs: ValidBusinessObjectList): Key[] => vobs.map((vob) => getDynamoKeyFromNodeOrEdge(vob))

export const extractAllStateAttributes = function (this: ValidBusinessObject | undefined): MapLike<any> {
  const metrics = this?.payload?.state?.reported
  const calculated = this?.payload?.state?.calculated
  return Object.assign({}, metrics, calculated)
}

export const getPositionFromTSS = (item: ValidBusinessObject | undefined) => {
  const position = item?.payload?.state?.reported?.position || item?.state?.reported?.position
  return position
}

export const extractPathFromOrderedThingStateShadowZombies = (tssZombies: MapLike<any>[]) => {
  const track = tssZombies.map((tss) => tss?.payload?.state?.reported?.position).filter((latLon) => isGeofencePointsType(latLon)) as GeofencePointsType[]
  return track
}

export const isLocatable = (item: ValidBusinessObject | undefined) => {
  return item && (isPosition(item.location) || isIGeofence(item))
}

export const getValueLabel = (text: string) =>
  text
    .toLowerCase()
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ')

export const getTotal = (states: ThingShadowStateZombie[], key: string) => {
  let initial = 0,
    count = 0,
    noCount = 0
  const reducer = (initial: number, tss: ThingShadowStateZombie, i: number) => {
    const metrics = extractAllStateAttributes.call(tss)
    if (metrics[key] && typeof metrics[key] === 'number') {
      count++
      return (initial += metrics[key])
    } else {
      noCount++
      return initial
    }
  }
  const total = states.reduce(reducer, initial)
  return { total, count, noCount }
}

export const getAverage = (states: ThingShadowStateZombie[], key: string) => {
  const totalPlus = getTotal(states, key)
  const average = totalPlus.count === 0 ? 0 : totalPlus.total / totalPlus.count
  return { average, ...totalPlus }
}

export const getTrackLength = (states: ThingShadowStateZombie[]) => {
  const n = 3 // approx 1m
  const gPath = extractPathFromOrderedThingStateShadowZombies(states)
  const tPath = convertGPointsToTPosition(gPath)
  debug && console.log('tPath is', tPath)
  let d = 0
  if (tPath instanceof Array && tPath.length > 0) {
    const line = lineString(tPath)
    d = length(line, { units: 'kilometers' })
  }

  return roundDecimals(d, n)
}
export const commaStringNumber = (number: Number) => {
  let x = number.toString()
  const pattern = /(-?\d+)(\d{3})/
  while (pattern.test(x)) x = x.replace(pattern, '$1,$2')
  return x
}

export const roundDecimals = (n: number, d: number) => Math.round(n * Math.pow(10, d)) / Math.pow(10, d)

/**
 * Function to round a number to x significant figures
 * @param n number to round
 * @param sig number of significant figures. Default is 3
 * @param number if you want a string or number result. Default is string
 * @returns
 */
export const significantFigures = (n: number, sig: number = 3, number: boolean = false) => {
  if (sig === 0) {
    return n
  } else {
    let res = Number(n.toPrecision(sig))
    if (number) {
      return res
    }
    return res.toLocaleString()
  }
}

export type PredicatableDeviceZombiesRequest = {
  deviceType: DeviceTypes
  appID: string // the NAS AppId
  _permittedAWOL?: number
  networkKey: Networks
  numberOfDevices: number
  devEuiLength: 8 | 16
  deviceEUIStart?: number
}
/** @deprecated - do not use except in testing/utilities.
 * THIS IS NOT SAFE WITH EUI NUMBER VALUES PARSED FROM STRINGS WHERE RESULT IS GREATER THAN Number.MAX_SAFE_INTERGER! */
export const getPredictableDeviceZombies = ({
  deviceType,
  appID,
  networkKey,
  _permittedAWOL,
  numberOfDevices,
  devEuiLength = 16,
  deviceEUIStart = 0x451000000,
}: PredicatableDeviceZombiesRequest) => {
  const deviceNamePrefix = 'Device'
  const mutateZombie = (zombie: ValidBusinessObject, idx: number) => {
    const eui = getNLengthHex(devEuiLength, deviceEUIStart + idx)
    const name = `${deviceNamePrefix} ${eui}`

    const appKey = getNLengthHex(32, 99999)
    const appEui = getNLengthHex(16, Math.round(Math.random() * 100000000000000))
    const deviceId = networkKey === Networks.AWS_LPWAN ? v4() : eui.substring(eui.length - 4)

    const mutant = reKeyItem(zombie, eui)
    Object.assign(mutant, { name, eui, appID, networkKey, appEui, appKey, deviceId, _permittedAWOL })
    return mutant
  }
  const zombieDevices = getPredictableZombies('Device', numberOfDevices, { deviceType })
  return zombieDevices.map((dev, i) => mutateZombie(dev, i))
}

export type GetDeviceZombiesRequest = {
  deviceType: DeviceTypes
  appID?: string
  /** a hex eui  */
  eui?: number
  networkKey: Networks
  _permittedAWOL?: number
}

/** @deprecated - do not use except in testing/utilities.
 * THIS IS NOT SAFE WITH EUI NUMBER VALUES PARSED FROM STRINGS WHERE RESULT IS GREATER THAN Number.MAX_SAFE_INTERGER! */
export const getDeviceZombie = ({ deviceType, eui, appID = 'MockApp', networkKey, _permittedAWOL }: GetDeviceZombiesRequest) => {
  return getPredictableDeviceZombies({ appID, devEuiLength: 16, deviceEUIStart: eui, deviceType, networkKey, numberOfDevices: 1, _permittedAWOL })[0]
}
